import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSupportEnabled } from "@/hooks/useSupportEnabled";
import { usePrivateMode, useStripeEnabled } from "@/lib/privateMode";
import { api } from "@/lib/apiClient";
import {
  SignOut, CaretDown, Plus, Check, Desktop, UsersThree, CreditCard, Buildings, Crown,
  WarningCircle, CheckCircle, Lifebuoy,
} from "@phosphor-icons/react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const PLAN_BADGE = {
  personal: "text-zinc-400 border-white/10 bg-white/5",
  business: "text-blue-400 border-blue-500/30 bg-blue-500/10",
  enterprise: "text-amber-400 border-amber-500/30 bg-amber-500/10",
  internal: "text-fuchsia-400 border-fuchsia-500/30 bg-fuchsia-500/10",
};

export function AppShell({ children }) {
  const { user, logout, orgs, currentOrg, setCurrentOrgId, refreshOrgs } = useAuth();
  const { enabled: supportEnabled } = useSupportEnabled();
  const priv = usePrivateMode();
  const stripeOn = useStripeEnabled();
  const navigate = useNavigate();
  const location = useLocation();
  const [createOpen, setCreateOpen] = useState(false);
  const [orgName, setOrgName] = useState("");

  const navItems = [
    { to: "/dashboard", label: "Machines", icon: Desktop },
    ...(supportEnabled ? [{ to: "/helpdesk", label: "Support", icon: Lifebuoy }] : []),
    { to: "/team", label: "Team", icon: UsersThree },
    ...(priv || !stripeOn ? [] : [{ to: "/billing", label: "Billing", icon: CreditCard }]),
    ...(user?.is_super_admin ? [{ to: "/admin", label: "Admin", icon: Crown }] : []),
  ];

  const createOrg = async () => {
    if (!orgName.trim()) return;
    try {
      const res = await api.post("/orgs", { name: orgName.trim() });
      await refreshOrgs();
      setCurrentOrgId(res.data.org_id);
      setCreateOpen(false);
      setOrgName("");
      toast.success("Team created");
      navigate("/dashboard");
    } catch {
      toast.error("Could not create team");
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header data-testid="app-header" className="border-b border-white/10 flex flex-wrap md:flex-nowrap items-center justify-between gap-x-2 gap-y-1 px-4 py-2 md:py-0 md:h-16 md:px-6 shrink-0">
        <div className="flex items-center gap-3 md:gap-4 min-w-0">
          <button
            onClick={() => navigate("/")}
            data-testid="home-logo-button"
            aria-label="Go to home"
            className="flex items-center gap-2.5 group"
          >
            <img src="/controlly-icon.png" alt="Controlly" className="w-9 h-9 rounded-md drop-shadow-[0_2px_8px_rgba(37,120,255,0.4)] group-hover:scale-105 transition-transform" />
            <span className="font-heading text-base font-extrabold tracking-tight hidden sm:block">Controlly</span>
          </button>

          {/* Org switcher */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="org-switcher" className="flex items-center gap-2 px-3 h-9 rounded-md border border-white/10 hover:bg-zinc-900 transition-colors min-w-0">
                <Buildings size={16} className="text-zinc-400" />
                <span className="text-sm font-medium max-w-[150px] md:max-w-[140px] truncate">{currentOrg?.name || "Workspace"}</span>
                {currentOrg && (
                  <span className={`hidden sm:inline text-[10px] px-1.5 py-0.5 rounded border font-mono uppercase ${PLAN_BADGE[currentOrg.plan]}`}>
                    {currentOrg.plan === "internal" ? "unlimited" : currentOrg.plan}
                  </span>
                )}
                <CaretDown size={14} className="text-zinc-500" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="bg-zinc-900 border-white/10 w-64">
              <DropdownMenuLabel className="font-mono text-xs uppercase tracking-wider text-zinc-500">Workspaces</DropdownMenuLabel>
              {orgs.map((o) => (
                <DropdownMenuItem
                  key={o.org_id}
                  data-testid={`org-option-${o.org_id}`}
                  onClick={() => { setCurrentOrgId(o.org_id); navigate("/dashboard"); }}
                  className="cursor-pointer flex items-center justify-between"
                >
                  <span className="truncate">{o.name}</span>
                  {o.org_id === currentOrg?.org_id && <Check size={15} className="text-blue-500" />}
                </DropdownMenuItem>
              ))}
              {!priv && <>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem data-testid="create-team-button" onClick={() => setCreateOpen(true)} className="cursor-pointer text-blue-400 focus:text-blue-400">
                  <Plus size={15} className="mr-2" /> New team
                </DropdownMenuItem>
              </>}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="contents md:flex md:items-center md:gap-2">
          <nav data-testid="app-nav" className="order-last md:order-none basis-full md:basis-auto flex items-center justify-around md:justify-start gap-1 md:mr-2 border-t border-white/5 pt-1 md:border-0 md:pt-0">
            {navItems.map((item) => {
              const active = location.pathname === item.to;
              const Icon = item.icon;
              return (
                <button
                  key={item.to}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  onClick={() => navigate(item.to)}
                  className={`flex items-center gap-2 px-3 h-9 rounded-md text-sm transition-colors ${
                    active ? "bg-zinc-800 text-white" : "text-zinc-400 hover:text-white hover:bg-zinc-900"
                  }`}
                >
                  <Icon size={16} weight={active ? "fill" : "regular"} />
                  <span className="hidden md:block">{item.label}</span>
                </button>
              );
            })}
          </nav>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu-button" className="flex items-center gap-2 hover:bg-zinc-900 rounded-md p-1.5 transition-colors">
                <Avatar className="w-8 h-8">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="bg-zinc-800 text-xs">{user?.name?.[0]}</AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-zinc-900 border-white/10 w-56">
              <DropdownMenuLabel>
                <div className="font-medium text-sm">{user?.name}</div>
                <div className="text-xs text-zinc-500 font-mono">{user?.email}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-white/10" />
              <DropdownMenuItem data-testid="logout-button" onClick={logout} className="cursor-pointer">
                <SignOut size={16} className="mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {!priv && user?.is_super_admin && user?.stripe_mode && user.stripe_mode !== "live" && (
        <div
          data-testid="stripe-mode-banner"
          className="bg-amber-500/10 border-b border-amber-500/25 text-amber-300 px-4 md:px-6 py-2 flex flex-wrap items-center justify-center gap-x-2 gap-y-0.5 text-xs md:text-sm"
        >
          <WarningCircle size={16} weight="fill" className="shrink-0" />
          <span className="text-center min-w-0">
            {user.stripe_mode === "unconfigured"
              ? "Stripe isn't configured yet. Customers can't subscribe."
              : "Payments are in TEST mode. No real charges. Add a live key before launching."}
          </span>
          <button
            data-testid="stripe-banner-manage"
            onClick={() => navigate("/admin")}
            className="md:ml-1 underline underline-offset-2 hover:text-amber-200 font-medium whitespace-nowrap"
          >
            Manage Stripe keys
          </button>
        </div>
      )}
      {!priv && user?.is_super_admin && user?.stripe_mode === "live" && (
        <div
          data-testid="stripe-mode-banner"
          className="bg-emerald-500/8 border-b border-emerald-500/20 text-emerald-400/90 px-4 md:px-6 py-1.5 flex items-center justify-center gap-1.5 text-xs text-center"
        >
          <CheckCircle size={13} weight="fill" className="shrink-0" />
          <span>Stripe is in LIVE mode. Real payments are enabled.</span>
        </div>
      )}

      {currentOrg?.suspended && (
        <div data-testid="org-suspended-banner" className="bg-red-500/10 border-b border-red-500/25 text-red-300 px-4 md:px-6 py-2 text-xs md:text-sm text-center">
          This organization is suspended. Its data is preserved, but access is paused. Contact your Controlly administrator.
        </div>
      )}
      <main className="flex-1">{children}</main>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-zinc-900 border-white/10" data-testid="create-team-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl tracking-tight">Create a team</DialogTitle>
            <DialogDescription className="text-zinc-400 text-sm">
              A team lets you share a pool of machines with members and manage access.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label className="text-sm text-zinc-300">Team name</Label>
            <Input
              data-testid="team-name-input"
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && createOrg()}
              placeholder="e.g. Acme IT"
              className="mt-2 bg-black border-zinc-800 focus-visible:ring-blue-600"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreateOpen(false)} className="text-zinc-400">Cancel</Button>
            <Button data-testid="confirm-create-team" onClick={createOrg} disabled={!orgName.trim()} className="bg-blue-600 hover:bg-blue-500">Create team</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
